/**
 * IndexedDB Cleanup Utility
 * 
 * This utility ONLY deletes the 'datalyze-storage' IndexedDB database that was
 * previously used by redux-persist-idb-storage for persisting cleaned datasets
 * and completed charts.
 * 
 * IMPORTANT: This utility ONLY targets 'datalyze-storage' and will NOT delete
 * any other IndexedDB databases. It is safe to run and will not affect other
 * applications or browser extensions that use IndexedDB.
 * 
 * Called on app initialization to ensure old IndexedDB data is removed.
 */

/**
 * Deletes the 'datalyze-storage' IndexedDB database
 * @returns {Promise<void>}
 */
const deleteDatalyzeStorage = () => {
    return new Promise((resolve) => {
        if (!window.indexedDB) {
            console.log('[IndexedDB Cleanup] IndexedDB not supported, skipping cleanup');
            resolve();
            return;
        }

        // ONLY delete 'datalyze-storage' - no other databases
        const dbName = 'datalyze-storage';
        const deleteRequest = indexedDB.deleteDatabase(dbName);

        deleteRequest.onsuccess = () => {
            console.log(`[IndexedDB Cleanup] Successfully deleted database: ${dbName}`);
            resolve();
        };

        deleteRequest.onerror = (event) => {
            console.warn(`[IndexedDB Cleanup] Error deleting database ${dbName}:`, event);
            // Don't throw - cleanup failure shouldn't break the app
            resolve();
        };

        deleteRequest.onblocked = () => {
            console.warn(`[IndexedDB Cleanup] Database ${dbName} is blocked (may be open in another tab)`);
            // Still resolve - blocked doesn't mean failure
            resolve();
        };
    });
};

/**
 * Cleans up ONLY the 'datalyze-storage' IndexedDB database
 * 
 * This function explicitly targets ONLY 'datalyze-storage' and will NOT
 * delete any other IndexedDB databases, ensuring safety for other applications.
 */
export const cleanupIndexedDB = async () => {
    try {
        console.log('[IndexedDB Cleanup] Starting cleanup - targeting ONLY "datalyze-storage" database');

        // ONLY delete 'datalyze-storage' - explicitly no other databases
        await deleteDatalyzeStorage();

        console.log('[IndexedDB Cleanup] Cleanup completed - only "datalyze-storage" was targeted');
    } catch (error) {
        console.error('[IndexedDB Cleanup] Error during cleanup:', error);
        // Don't throw - cleanup failure shouldn't break the app
    }
};
